import React, { useState, useRef } from "react";
import "./StoreSelectionCard.css";
import TelegramText from "../Text/TelegramText";
import TelegramButton from "../Button/TelegramButton";
import GirlAnimation from "../Loader/GirlAnimation";
import useRandomFooterIcon from "../../../hooks/useRandomFooterIcon";
import ItemTooltip from "../ItemTooltip/ItemTooltip";

function StoreSelectionCard({
  currentSelectedStore,
  onTreeChangeClick,
  onMainClick,
}) {
  // Use randomized footer icons - separate instances for different randomization
  const topIcons = useRandomFooterIcon("top");
  const bottomIcons = useRandomFooterIcon("bottom");
  
  // State for item tooltip
  const [itemTooltip, setItemTooltip] = useState({
    isVisible: false,
    anchorElement: null,
    itemId: null,
    itemName: '',
    itemDescription: '',
    position: 'bottom-left'
  });
  
  // Map footer icons to item IDs
  const getItemIdForIcon = (iconName) => {
    // Map specific icons to items, or use a random assignment
    const iconToItemMap = {
      'serena': 'item-1',
      'lil_pony': 'item-2',
      'spark_dragon': 'item-3',
      'flamehorn_dragon': 'item-4',
      'armored_dragon': 'item-5',
      'hammer_dragon': 'item-6',
      'bruiser_dragon': 'item-1',
      'sulking_ghost': 'item-2',
      'orange_mushroom': 'item-3',
      'zeal_blasphemy': 'item-4',
    };
    
    // Extract base name from icon path
    const baseName = iconName.toLowerCase()
      .replace('stand0_0_', '')
      .replace('stand_0_', '')
      .replace('.png', '')
      .replace(/ /g, '_');
    
    return iconToItemMap[baseName] || `item-${Math.floor(Math.random() * 6) + 1}`;
  };
  
  // Handle footer icon click
  const handleFooterIconClick = (event, icon) => {
    event.stopPropagation();
    
    const anchorElement = event.currentTarget;
    const rect = anchorElement.getBoundingClientRect();
    
    // Determine position based on icon location
    const position = rect.left > window.innerWidth / 2 ? 'bottom-right' : 'bottom-left';
    
    const itemId = getItemIdForIcon(icon.name);
    
    setItemTooltip({
      isVisible: true,
      anchorElement: anchorElement,
      itemId: itemId,
      itemName: icon.alt || icon.name,
      itemDescription: `Special item: ${icon.alt || icon.name}`,
      position: position
    });
  };
  
  // Hide item tooltip
  const hideItemTooltip = () => {
    setItemTooltip(prev => ({ ...prev, isVisible: false }));
  };

  // Helper function to render randomized footer icons
  const renderFooterIcons = (iconInstance = topIcons) => {
    if (iconInstance.isLoading) {
      return (
        <TelegramText className="login-text telegramSubtitle">
          {iconInstance.fallbackText}
        </TelegramText>
      );
    }

    return (
      <div className="footer-icons-container">
        {iconInstance.iconPaths.map((icon, index) => (
          <img
            key={`${icon.name}-${index}`}
            src={icon.path}
            alt={icon.alt}
            className="footer-action-icon"
            onClick={(e) => handleFooterIconClick(e, icon)}
            onError={(e) => {
              // Hide broken images and show fallback if all fail
              e.target.style.display = "none";
              const container = e.target.parentElement;
              const visibleIcons = container.querySelectorAll(
                'img:not([style*="display: none"])'
              );
              if (visibleIcons.length === 0) {
                container.innerHTML = `<span class="login-text telegramSubtitle">${iconInstance.fallbackText}</span>`;
              }
            }}
          />
        ))}
      </div>
    );
  };

  // Helper function to render store icon (emoji or sprite)
  const renderStoreIcon = () => {
    const { storeEmoji, storeName } = currentSelectedStore;
    if (storeEmoji && storeEmoji.startsWith("/")) {
      // Image sprite
      return (
        <img
          src={storeEmoji}
          alt={storeName}
          className="store-selection-sprite"
          style={{
            width: "24px",
            height: "24px",
            imageRendering: "pixelated",
            imageRendering: "-moz-crisp-edges",
            imageRendering: "crisp-edges",
            marginRight: "8px",
            verticalAlign: "middle",
          }}
        />
      );
    } else {
      // Regular emoji
      return <span style={{ marginRight: "8px" }}>{storeEmoji}</span>;
    }
  };

  return (
    <div className="StoreSelectionCard">
      <div className="store__selection__card__container">
        {/* Left content area - World button with title/subtitle */}
        <div className="store__selection__card__content__container">
          <TelegramText className="telegramTitle">START WORLD</TelegramText>
          {/* <TelegramText className="center__items telegramSubtitle">
                  Train with us everyday!
                </TelegramText> */}
          {renderFooterIcons()}

          <div className="store-world-selection-container">
            <div className="store__world__change__container">
              <TelegramButton
                className="store__world__change__button"
                onClick={onTreeChangeClick}
              >
                World Change
              </TelegramButton>
            </div>
            <TelegramButton
              className="gradient-button selected__store__button small-font"
              onClick={onMainClick}
            >
              {/* {renderStoreIcon()} */}
              {currentSelectedStore.storeName}
            </TelegramButton>
            {renderFooterIcons(bottomIcons)}
          </div>
        </div>

        {/* Right image area - Aquila character */}
        <div className="store__selection__card__image__container">
          <GirlAnimation speed={400} size={120} />
          {/* Fallback if animation fails */}
          <img
            src="/girl_animation_2/stand1_0.png"
            alt="Aquila World"
            style={{
              width: "120px",
              height: "120px",
              display: "none",
              imageRendering: "pixelated",
            }}
            onError={(e) => {
              e.target.style.display = "block";
              console.error("GirlAnimation fallback triggered");
            }}
          />
        </div>
      </div>
      
      {/* Item Tooltip */}
      <ItemTooltip
        isVisible={itemTooltip.isVisible}
        anchorElement={itemTooltip.anchorElement}
        itemId={itemTooltip.itemId}
        itemName={itemTooltip.itemName}
        itemDescription={itemTooltip.itemDescription}
        position={itemTooltip.position}
        onClose={hideItemTooltip}
      />
    </div>
  );
}

export default StoreSelectionCard;
